import type { StencilRotation } from '../../model/recipe';
import { GLYPH_IDS, ICON_IDS } from './bitmaps';
import { PROCEDURAL_SHAPE_IDS } from './procedural';
import { stencilSolidity } from './solidity';

/**
 * Versioned stencil manifest.
 *
 * Selection is driven by stable string ids, never by array position or directory order, so
 * adding a stencil in a later library version cannot reshuffle previously released levels.
 * See docs/STENCILS.md.
 */
export interface StencilManifestEntry {
  readonly id: string;
  /** Where the mask comes from: `procedural:<shape>`, `icon:<name>` or `glyph:<char>`. */
  readonly source: string;
  readonly category: string;
  readonly title: string;
  readonly preferredAspectRatio?: number;
  readonly allowedRotations: readonly StencilRotation[];
  readonly allowHorizontalMirror: boolean;
  readonly allowVerticalMirror: boolean;
  readonly minimumGridWidth: number;
  readonly minimumGridHeight: number;
  readonly maximumGridWidth?: number;
  readonly maximumGridHeight?: number;
  /** Minimum fraction of grid cells the silhouette must cover to be usable. */
  readonly minimumUsableRatio: number;
  readonly complexityRating?: number;
  readonly tags: readonly string[];
  /** First content version in which this stencil may be selected. */
  readonly contentVersion: number;
}

const PROCEDURAL_META: Record<
  string,
  {
    title: string;
    complexity: number;
    ratio?: number;
    rotations?: StencilRotation[];
    tags: string[];
  }
> = {
  rectangle: { title: 'Block', complexity: 1, tags: ['simple', 'dense'] },
  disc: { title: 'Disc', complexity: 2, tags: ['simple', 'round'] },
  diamond: { title: 'Diamond', complexity: 2, tags: ['simple', 'angular'] },
  ring: { title: 'Ring', complexity: 4, tags: ['hollow', 'round'] },
  cross: { title: 'Cross', complexity: 3, tags: ['corridor', 'angular'] },
  chevron: { title: 'Chevron', complexity: 3, rotations: [0, 90, 180, 270], tags: ['corridor'] },
  triangle: { title: 'Wedge', complexity: 2, rotations: [0, 90, 180, 270], tags: ['angular'] },
  hourglass: { title: 'Hourglass', complexity: 3, ratio: 0.8, tags: ['symmetric'] },
  spiral: { title: 'Coil', complexity: 5, ratio: 1, tags: ['corridor', 'complex'] },
  arch: { title: 'Arch', complexity: 4, tags: ['hollow'] },
  wave: { title: 'Wave', complexity: 3, ratio: 1.6, tags: ['corridor'] },
  frame: { title: 'Frame', complexity: 3, tags: ['hollow', 'perimeter'] },
  pill: { title: 'Capsule', complexity: 2, tags: ['simple', 'round'] },
  stairs: { title: 'Steps', complexity: 3, rotations: [0, 90, 180, 270], tags: ['angular'] },
  hexagon: { title: 'Hex', complexity: 2, tags: ['simple', 'dense', 'solid'] },
  octagon: { title: 'Octagon', complexity: 2, tags: ['simple', 'dense', 'solid'] },
  squircle: { title: 'Rounded', complexity: 1, tags: ['simple', 'dense', 'solid'] },
  shield: { title: 'Shield', complexity: 2, tags: ['dense', 'solid'] },
  egg: { title: 'Egg', complexity: 2, tags: ['dense', 'solid', 'round'] },
  trapezoid: { title: 'Wedge block', complexity: 2, tags: ['dense', 'solid', 'angular'] },
};

const ICON_META: Record<string, { title: string; complexity: number }> = {
  heart: { title: 'Heart', complexity: 3 },
  star: { title: 'Star', complexity: 5 },
  house: { title: 'House', complexity: 4 },
  tree: { title: 'Tree', complexity: 4 },
  moon: { title: 'Crescent', complexity: 4 },
  cloud: { title: 'Cloud', complexity: 3 },
  flower: { title: 'Bloom', complexity: 5 },
  key: { title: 'Key', complexity: 5 },
  boat: { title: 'Sailboat', complexity: 5 },
  mountain: { title: 'Peak', complexity: 3 },
};

function buildManifest(): StencilManifestEntry[] {
  const entries: StencilManifestEntry[] = [];

  for (const shape of PROCEDURAL_SHAPE_IDS) {
    const meta = PROCEDURAL_META[shape];
    entries.push({
      id: `shape-${shape}`,
      source: `procedural:${shape}`,
      category: 'shape',
      title: meta.title,
      ...(meta.ratio === undefined ? {} : { preferredAspectRatio: meta.ratio }),
      allowedRotations: meta.rotations ?? [0],
      allowHorizontalMirror: true,
      allowVerticalMirror: true,
      minimumGridWidth: 6,
      minimumGridHeight: 6,
      minimumUsableRatio: shape === 'ring' || shape === 'frame' || shape === 'spiral' ? 0.18 : 0.25,
      complexityRating: meta.complexity,
      tags: meta.tags,
      contentVersion: 1,
    });
  }

  for (const icon of ICON_IDS) {
    const meta = ICON_META[icon];
    entries.push({
      id: `icon-${icon}`,
      source: `icon:${icon}`,
      category: 'icon',
      title: meta.title,
      preferredAspectRatio: 1,
      allowedRotations: [0],
      allowHorizontalMirror: true,
      allowVerticalMirror: false,
      minimumGridWidth: 9,
      minimumGridHeight: 9,
      minimumUsableRatio: 0.2,
      complexityRating: meta.complexity,
      tags: ['icon', 'showcase'],
      contentVersion: 1,
    });
  }

  for (const glyph of GLYPH_IDS) {
    entries.push({
      id: `glyph-${glyph}`,
      source: `glyph:${glyph}`,
      category: 'glyph',
      title: `Glyph ${glyph}`,
      preferredAspectRatio: 5 / 7,
      allowedRotations: [0],
      allowHorizontalMirror: false,
      allowVerticalMirror: false,
      minimumGridWidth: 8,
      minimumGridHeight: 10,
      minimumUsableRatio: 0.16,
      complexityRating: 3,
      tags: ['glyph', 'showcase'],
      contentVersion: 1,
    });
  }

  // Stable ordering by id: selection must never depend on construction order.
  return entries.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

export const STENCIL_MANIFEST: readonly StencilManifestEntry[] = buildManifest();

const BY_ID = new Map(STENCIL_MANIFEST.map((entry) => [entry.id, entry]));

export function getStencil(id: string): StencilManifestEntry | undefined {
  return BY_ID.get(id);
}

export function requireStencil(id: string): StencilManifestEntry {
  const entry = BY_ID.get(id);
  if (entry === undefined) throw new Error(`Unknown stencil id: ${id}`);
  return entry;
}

export interface StencilFilter {
  readonly categories?: readonly string[];
  /**
   * Reject silhouettes thinner than this. Thin stencils spread arrows across many rows and
   * columns, which produces boards with almost no entanglement; see `solidity.ts`.
   */
  readonly minimumSolidity?: number;
}

/** Stencils selectable for a grid size at a given content version, in stable id order. */
export function eligibleStencils(
  gridWidth: number,
  gridHeight: number,
  contentVersion: number,
  filter: StencilFilter = {},
): StencilManifestEntry[] {
  return STENCIL_MANIFEST.filter((entry) => {
    if (entry.contentVersion > contentVersion) return false;
    if (filter.categories !== undefined && !filter.categories.includes(entry.category))
      return false;
    if (gridWidth < entry.minimumGridWidth || gridHeight < entry.minimumGridHeight) return false;
    if (entry.maximumGridWidth !== undefined && gridWidth > entry.maximumGridWidth) return false;
    if (entry.maximumGridHeight !== undefined && gridHeight > entry.maximumGridHeight) return false;
    if (filter.minimumSolidity !== undefined && stencilSolidity(entry) < filter.minimumSolidity) {
      return false;
    }
    return true;
  });
}

/** The always-available fallback; it fits every grid the game can produce. */
export const FALLBACK_STENCIL_ID = 'shape-rectangle';
